import { useState, useEffect } from 'react';
import WizardModal, { WizardButton, WizardLabel, WizardInput, WizardSelect, WizardSuccess, WizardError } from './WizardModal';
import useWizardSubmit from './useWizardSubmit';
import { get, del } from '../../api';

interface Preference {
  id: number;
  type: string;
  item: string;
  frequency: string;
  reason: string | null;
}

const TYPE_OPTIONS = [
  { value: 'like', label: 'Mag ich' },
  { value: 'dislike', label: 'Mag ich nicht' },
  { value: 'allergy', label: 'Allergie / Unverträglichkeit' },
];

const FREQUENCY_OPTIONS = [
  { value: 'frequent', label: 'Häufig' },
  { value: 'occasional', label: 'Gelegentlich' },
  { value: 'rarely', label: 'Selten' },
  { value: 'never', label: 'Nie' },
  { value: 'exclude', label: 'Ausschließen' },
];

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function PreferencesWizard({ open, onClose }: Props) {
  const [type, setType] = useState('like');
  const [item, setItem] = useState('');
  const [frequency, setFrequency] = useState('frequent');
  const [reason, setReason] = useState('');
  const [existing, setExisting] = useState<Preference[]>([]);
  const { status, error, submit, reset } = useWizardSubmit();

  const loadPreferences = () => {
    get<Preference[]>('/preferences').then(setExisting).catch(() => {});
  };

  useEffect(() => {
    if (open) loadPreferences();
  }, [open]);

  const handleSubmit = async () => {
    if (!item.trim()) return;
    const result = await submit('/preferences', {
      type,
      item: item.trim(),
      frequency,
      reason: reason || undefined,
    });
    if (result) {
      setItem('');
      setReason('');
      loadPreferences();
      // Auto-reset for adding more
      setTimeout(() => reset(), 1500);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await del(`/preferences/${id}`);
      setExisting(prev => prev.filter(p => p.id !== id));
    } catch { /* ignore */ }
  };

  const handleClose = () => {
    setItem('');
    setReason('');
    reset();
    onClose();
  };

  return (
    <WizardModal
      open={open}
      onClose={handleClose}
      title="Präferenzen"
      footer={
        <WizardButton onClick={handleSubmit} loading={status === 'loading'} disabled={!item.trim()}>
          Hinzufügen
        </WizardButton>
      }
    >
      {error && <WizardError message={error} />}
      {status === 'success' && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2 text-sm text-emerald-700">
          Gespeichert!
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div>
          <WizardLabel>Typ</WizardLabel>
          <WizardSelect value={type} onChange={setType} options={TYPE_OPTIONS} />
        </div>
        <div>
          <WizardLabel>Häufigkeit</WizardLabel>
          <WizardSelect value={frequency} onChange={setFrequency} options={FREQUENCY_OPTIONS} />
        </div>
      </div>

      <div>
        <WizardLabel>Lebensmittel</WizardLabel>
        <WizardInput
          value={item}
          onChange={setItem}
          placeholder="z.B. Tofu, Nüsse, Brokkoli"
          autoFocus
        />
      </div>

      <div>
        <WizardLabel>Grund (optional)</WizardLabel>
        <WizardInput value={reason} onChange={setReason} placeholder="z.B. Geschmack, Unverträglichkeit" />
      </div>

      {/* Existing preferences */}
      {existing.length > 0 && (
        <div>
          <WizardLabel>Aktuelle Präferenzen</WizardLabel>
          <div className="space-y-1 mt-1 max-h-40 overflow-y-auto">
            {existing.map(p => (
              <div key={p.id} className="flex items-center justify-between bg-gray-100 rounded-lg px-3 py-1.5">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-xs shrink-0">
                    {p.type === 'like' ? '👍' : p.type === 'dislike' ? '👎' : '⚠️'}
                  </span>
                  <span className="text-sm text-gray-700 truncate">{p.item}</span>
                  <span className="text-[10px] text-gray-400 shrink-0">{p.frequency}</span>
                </div>
                <button
                  onClick={() => handleDelete(p.id)}
                  className="text-gray-400 hover:text-red-500 transition-colors shrink-0 ml-2"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </WizardModal>
  );
}
